// apps/web/src/lib/vendas/ajustes.ts — Fase 12, Plano 31 (PV-07, D-26, D-28, D-29, T-12-46).
// Regras puras dos ajustes do PV na tela: quais ações o item oferece (estado do item + permissão), cópias do contrato
// e o que vai ao servidor. A tela só ORIENTA: quem decide é o servidor (409 ITEM_JA_RECEBIDO, TROCA_MATERIAL_BLOQUEADA...).
import type { AjustarItemRecebidoInput, PedidoVendaItemView, PrioridadePv } from '@pieltec/contracts';
import { formatarMoeda, formatarQuantidade, unidadePorExtenso } from './formatacao';
import { erroQuantidadePv } from './validacao-pv';

/** Mesmo mínimo da API (`normalizarMotivo`): motivo com menos que isto volta 400. */
export const MOTIVO_MINIMO_CARACTERES = 5;

export function motivoValido(texto: string): boolean {
  return texto.trim().length >= MOTIVO_MINIMO_CARACTERES;
}

export type AcaoItemPv = 'EDITAR' | 'CANCELAR' | 'AJUSTAR' | 'ENCERRAR_SALDO';

export interface EntradaAcoesItem {
  /** O item já tem lote (recebido/em produção): decide entre editar (pv:editar) e ajustar (pv:ajustar). */
  readonly temLote: boolean;
  readonly cancelado?: boolean;
  readonly saldoEncerrado?: boolean;
  /** A faturar (`saldos.saldo`); ausente = não informado (a ação é oferecida e o servidor valida). */
  readonly saldo?: number;
  readonly permissoes: readonly string[];
}

/**
 * Ações do menu "⋯" do item. Item sem lote: editar/cancelar (pv:editar). Item com lote: ajustar e encerrar saldo
 * (pv:ajustar). Uma permissão não vale para o estado errado (T-12-46); só pv:ler = nenhuma ação.
 */
export function acoesDoItem({ temLote, cancelado = false, saldoEncerrado = false, saldo, permissoes }: EntradaAcoesItem): AcaoItemPv[] {
  if (cancelado) return [];
  if (!temLote) return permissoes.includes('pv:editar') ? ['EDITAR', 'CANCELAR'] : [];
  if (!permissoes.includes('pv:ajustar')) return [];
  const acoes: AcaoItemPv[] = ['AJUSTAR'];
  if (!saldoEncerrado && (saldo === undefined || saldo > 0)) acoes.push('ENCERRAR_SALDO');
  return acoes;
}

/** D-29: qualquer etapa apontada (apontamento, sessão ou inspeção) trava a troca de cor/material. */
export function trocaMaterialPermitida({ algumaEtapaApontada }: { readonly algumaEtapaApontada: boolean }): boolean {
  return !algumaEtapaApontada;
}

export function ajudaQuantidadeMinima(recebido: number, unidade = 'peça'): string {
  return `Não pode ser menor que o recebido (${formatarQuantidade(recebido, unidade)}).`;
}

/** Erro inline da quantidade do ajuste, ou null quando vale. */
export function validarQuantidadeAjuste(quantidade: number, recebido: number, unidade = 'peça', texto?: string): string | null {
  const erroBase = erroQuantidadePv(quantidade, texto);
  if (erroBase) return erroBase;
  if (quantidade < recebido) return ajudaQuantidadeMinima(recebido, unidade);
  return null;
}

/** Botão "Reprecificar pela tabela": mostra `{atual} → {tabela}` (preço em uso → preço vigente hoje). */
export function rotuloReprecificar(atual: number | null, tabela: number | null): string {
  return `${formatarMoeda(atual)} → ${formatarMoeda(tabela)}`;
}

/** Corpo do diálogo "Encerrar saldo" (UI-SPEC, D-26). */
export function corpoEncerrarSaldo({ saldo, numeroItem, unidade = 'peça' }: { readonly saldo: number; readonly numeroItem: number; readonly unidade?: string }): string {
  return `O saldo restante de ${formatarQuantidade(saldo, unidade)} ${unidadePorExtenso(saldo, unidade)} do item ${numeroItem} será encerrado sem faturamento (ex.: o cliente não vai enviar o restante). O motivo fica registrado. Confirmar?`;
}

/** WR-D1-13: "Salvar ajuste" não envia a troca de cor/material/roteiro; se há troca digitada e não aplicada, o usuário é avisado. */
export const COPIA_TROCA_PENDENTE =
  'Você alterou cor, material ou roteiro, mas não clicou em "Trocar cor/material". "Salvar ajuste" grava só quantidade, preço, prazo e prioridade: continuar descarta essa alteração.';

/** Corpo do diálogo destrutivo "Trocar cor/material" (UI-SPEC, D-29). */
export const COPIA_TROCAR_MATERIAL =
  'O lote e a OP deste item serão refeitos com o novo material e roteiro. Nenhuma etapa foi apontada. O motivo fica registrado na auditoria. Confirmar troca?';

export interface CamposAjusteItem {
  readonly quantidade: number;
  readonly precoUnitario: number | null;
  readonly previsaoEntrega: string | null;
  readonly prioridade: PrioridadePv;
}

export interface EntradaAjusteItem {
  readonly quantidade: number;
  /** Preço digitado; null = campo vazio (limpa o preço). */
  readonly preco: number | null;
  readonly previsao: string | null;
  readonly prioridade: PrioridadePv;
}

/** Só o que mudou em relação ao item (o servidor recusa "nada a alterar"); vazio limpa preço/prazo. */
export function diferencasAjuste(base: CamposAjusteItem, campos: EntradaAjusteItem): Omit<AjustarItemRecebidoInput, 'versao' | 'motivo'> {
  const corpo: Omit<AjustarItemRecebidoInput, 'versao' | 'motivo'> = {};
  if (campos.quantidade !== base.quantidade) corpo.quantidade = campos.quantidade;
  if (campos.preco !== base.precoUnitario) corpo.precoUnitario = campos.preco;
  if (campos.previsao !== base.previsaoEntrega) corpo.previsaoEntrega = campos.previsao;
  if (campos.prioridade !== base.prioridade) corpo.prioridade = campos.prioridade;
  return corpo;
}

/** Item ativo com saldo a encerrar (não cancelado, saldo não encerrado, saldo > 0). */
function temSaldoAEncerrar(item: PedidoVendaItemView): boolean {
  return !item.cancelado && !item.saldoEncerrado && item.saldos.saldo > 0;
}

/** "Encerrar saldo" do menu do PV: só age direto quando há exatamente um item com saldo; senão orienta pelo item. */
export function itemUnicoComSaldo(itens: readonly PedidoVendaItemView[]): PedidoVendaItemView | null {
  const candidatos = itens.filter(temSaldoAEncerrar);
  return candidatos.length === 1 ? (candidatos[0] ?? null) : null;
}

export function existeItemComSaldo(itens: readonly PedidoVendaItemView[]): boolean {
  return itens.some(temSaldoAEncerrar);
}

export const ORIENTACAO_ENCERRAR_SALDO_POR_ITEM =
  'Este PV tem mais de um item com saldo. Encerre o saldo pelo menu ⋯ de cada item.';
